import { useEffect, useState } from 'react';
import { Box, CircularProgress, Link, MenuItem, Select, Typography } from '@mui/material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { COLORS } from '../IndividualPlan/tokens';
import { ReportSections } from '../ProgramEvaluation';
import { ScoreUnitProvider } from '../ProgramEvaluation/ScoreUnitContext';
import { DataNotes } from '../ProgramEvaluation/components/DataNotes';
import { ProgramEvaluationHeader } from '../ProgramEvaluation/components/ProgramEvaluationHeader';
import { ScoreUnitToggle } from '../ProgramEvaluation/components/ScoreUnitToggle';
import {
  ContentColumn,
  DistrictHeader,
  ToggleBar,
} from '../ProgramEvaluation/program-evaluation.styled';
import { fetchPEReport, type PEReportArtifact, type PEReportVersion } from './peAdminApi';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

function versionLabel({ version, generatedAt }: PEReportVersion): string {
  const date = generatedAt ? new Date(generatedAt) : null;
  const formatted =
    date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: 'UTC',
        })
      : '';
  return formatted ? `Version ${version} · ${formatted}` : `Version ${version}`;
}

type Props = {
  source?: typeof fetchPEReport;
};

type FetchOutcome = { artifact: PEReportArtifact | null } | 'error';

export function AdminProgramEvaluationReportPage({ source = fetchPEReport }: Props = {}) {
  const { sub = '', year = '' } = useParams();
  const [version, setVersion] = useState<number | undefined>(undefined);
  const [result, setResult] = useState<{ key: string; outcome: FetchOutcome } | null>(null);
  const key = `${sub}#${year}#${version ?? 'latest'}`;

  useEffect(() => {
    let cancelled = false;
    source(sub, year, version)
      .then((artifact) => {
        if (!cancelled) setResult({ key, outcome: { artifact } });
      })
      .catch(() => {
        if (!cancelled) setResult({ key, outcome: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [source, sub, year, version, key]);

  const outcome = result?.key === key ? result.outcome : null;
  const failed = outcome === 'error';
  const artifact = outcome && outcome !== 'error' ? outcome.artifact : undefined;

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: COLORS.bg }}>
      <Sidebar sections={SIDEBAR_SECTIONS} />
      <Box component="main" sx={{ flex: 1, minWidth: 0, py: 4 }}>
        <ContentColumn>
          <Link
            component={RouterLink}
            to="/admin/program-evaluations"
            sx={{ fontSize: 14, display: 'inline-block', mb: 2 }}
          >
            All program evaluations
          </Link>
          {failed && (
            <Typography role="alert" sx={{ fontSize: 16, color: COLORS.ink }}>
              This program evaluation is temporarily unavailable.
            </Typography>
          )}
          {!failed && artifact === undefined && (
            <CircularProgress aria-label="Loading Program Evaluation report" />
          )}
          {!failed && artifact === null && (
            <Typography role="alert" sx={{ fontSize: 16, color: COLORS.ink }}>
              No program evaluation report exists for this school year.
            </Typography>
          )}
          {!failed && artifact && <ArtifactReport artifact={artifact} onPickVersion={setVersion} />}
        </ContentColumn>
      </Box>
    </Box>
  );
}

function ArtifactReport({
  artifact,
  onPickVersion,
}: {
  artifact: PEReportArtifact;
  onPickVersion: (version: number) => void;
}) {
  const versions = [...artifact.versions].sort((a, b) => b.version - a.version);
  const { report, content } = artifact;
  const cannotGenerate = Boolean(report?.cannot_generate);
  return (
    <>
      {report && !cannotGenerate ? (
        <DistrictHeader>
          <ProgramEvaluationHeader report={report} />
        </DistrictHeader>
      ) : (
        <Typography role="alert" sx={{ fontSize: 16, color: COLORS.ink }}>
          {report
            ? 'This Program Evaluation report cannot be generated.'
            : 'This version has no stored report payload.'}
        </Typography>
      )}
      {versions.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: COLORS.ink }}>
            Report version
          </Typography>
          <Select
            size="small"
            value={artifact.version}
            onChange={(event) => onPickVersion(Number(event.target.value))}
            inputProps={{ 'aria-label': 'Report version' }}
          >
            {versions.map((entry) => (
              <MenuItem key={entry.version} value={entry.version}>
                {versionLabel(entry)}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
      {report && !cannotGenerate && (
        <>
          <ScoreUnitProvider key={artifact.version} initialUnit={content?.unit_mode}>
            <ToggleBar>
              <ScoreUnitToggle />
            </ToggleBar>
            <ReportSections report={report} content={content} />
          </ScoreUnitProvider>
          <DataNotes notes={report.data_quality.notes} />
        </>
      )}
    </>
  );
}
